import { GetTaskGroupKey } from './createTaskGroupKeyResolver';

type LinkingInput = { id: string; unlink?: boolean };
type TaskFieldValue = { value: string };

// Single source of truth for the `taskGroupLinks` payload used by all four
// Documentation mutations (create, update, bulkEdit, importCreate). BE
// validates each `id` as a `Task.groupKey` of a task in the user's scope —
// never an occurrence `Task.id`.
//
// The modal-driven Tasks selector emits group_keys directly, so most calls
// pass values through unchanged. `getTaskGroupKey` is a defensive Apollo-
// cache lookup: when a value is still a per-occurrence Task.id (stale form
// state, partial cache, prefill edge case), it resolves to the matching
// groupKey. Values that resolve to nothing are dropped rather than sent as
// invalid ids.
//
// `prevValues` is only meaningful for DocumentForm edit mode — items that
// were previously linked but are missing from the current selection are
// emitted with `unlink: true` so the BE removes the link.
// Returns `null` (not `undefined`) when there are no links — matches the
// contract DocumentForm's spread relies on (`...(xxxLinks !== null && { xxxLinks })`).
//
// Behavior matches `mergeAndUnlinkMultiSelectValues`:
// - When `prevValues` is omitted (create / bulk-edit / import — no baseline
//   to diff against), every current id is emitted as `{ id }`.
// - When `prevValues` is provided (DocumentForm edit), only the delta is
//   emitted: ids new to the selection as `{ id }`, ids removed as
//   `{ id, unlink: true }`. Untouched ids are skipped so the BE doesn't
//   churn unchanged links.
export default function buildTaskGroupLinks({
  values,
  prevValues,
  getTaskGroupKey,
}: {
  values: TaskFieldValue[] | undefined;
  prevValues?: TaskFieldValue[];
  getTaskGroupKey?: GetTaskGroupKey;
}): LinkingInput[] | null {
  const resolveUnique = (list: TaskFieldValue[] | undefined): string[] => {
    if (!list?.length) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    list.forEach(({ value }) => {
      const resolved = getTaskGroupKey?.(value) ?? value;
      if (!resolved || seen.has(resolved)) return;
      seen.add(resolved);
      out.push(resolved);
    });
    return out;
  };

  const currentIds = resolveUnique(values);

  if (prevValues === undefined) {
    return currentIds.length ? currentIds.map((id) => ({ id })) : null;
  }

  const prevIds = resolveUnique(prevValues);
  const prevSet = new Set(prevIds);
  const currentSet = new Set(currentIds);
  const links: LinkingInput[] = [];
  currentIds.forEach((id) => {
    if (!prevSet.has(id)) links.push({ id });
  });
  prevIds.forEach((id) => {
    if (!currentSet.has(id)) links.push({ id, unlink: true });
  });
  return links.length ? links : null;
}
